import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import {
    Alert,
    Nav,
    NavItem,
    NavLink,
    Row,
    TabContent,
    TabPane,
} from "reactstrap";
import classnames from "classnames";
import LicenseManagementService from "src/modules/3rd-party-management/apis/admin/LicenseManagementService";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import LoadingContent from "../kyb-common-components/loading-content";
import CreditCards from "../kyb-common-components/credit-cards";
import CreditTransactions from "../kyb-common-components/credit-transactions";
import CreditUsageTable from "../kyb-common-components/usage-table";
import Dnb from "../dnb";

const SCREENING_TRANSACTION_TYPES = [
    "compliance_persons_search",
    "sanction_data",
    "possible_persons",
    "possible_companies",
];

const MONITORING_TRANSACTION_TYPES = [
    "monitoring_add_follow_list_item",
];

const emptyUsage = {
    totalCredit: 0,
    usageCount: 0,
    remaining: 0,
};

const getPoolUsage = (usageCounts = {}, poolKey) => {
    if (usageCounts[poolKey]) {
        return usageCounts[poolKey];
    }

    // Older APIs only return a combined compliance pool
    if (poolKey === "screening" && usageCounts.compliance) {
        return usageCounts.compliance;
    }

    return emptyUsage;
};

const getPoolTransactions = (creditTransactions = {}, poolKey) => {
    if (Array.isArray(creditTransactions?.[poolKey])) {
        return creditTransactions[poolKey];
    }

    const complianceTransactions = Array.isArray(creditTransactions?.compliance)
        ? creditTransactions.compliance
        : [];

    const allowedTypes = poolKey === "monitoring"
        ? MONITORING_TRANSACTION_TYPES
        : SCREENING_TRANSACTION_TYPES;

    return complianceTransactions.filter((tx) =>
        allowedTypes.includes(tx?.data_type)
    );
};

const CompliancePoolContent = ({
    poolKey,
    showUsageTable = false,
    fetchQuery: {
        data: overviewData,
        isFetching: isFetchingOverview,
    },
}) => {
    if (isFetchingOverview) {
        return <LoadingContent />;
    }

    const { usageCounts = {}, creditTransactions } = overviewData || {};
    const usage = getPoolUsage(usageCounts, poolKey);

    return (
        <Row>
            <CreditCards
                totalCredits={usage?.totalCredit || 0}
                totalUsage={usage?.usageCount || 0}
                remaining={usage?.remaining || 0}
            />
            {showUsageTable ? (
                <CreditUsageTable
                    provider="company_info"
                    pool={poolKey}
                />
            ) : null}
            <CreditTransactions
                transactions={getPoolTransactions(creditTransactions, poolKey)}
            />
        </Row>
    );
};

const CompanyInfo = ({ t }) => {
    const [activeTab, setActiveTab] = useState("screening");

    const fetchQuery = useQuery({
        queryKey: [
            "3rd-party-management-fetch-organization-license-overview",
            "companyinfo",
        ],
        queryFn: async () => {
            const service = LicenseManagementService.getInstance();

            return await service.fetchkYBLicenseDetail({
                provider: "company_info",
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: isFeatureEnabledForActiveModule("kyb_activation"),
        onError: () => {
            toast(t("An error occurred while fetching overview content."), {
                type: "error",
            });
        },
    });

    if (!isFeatureEnabledForActiveModule("kyb_activation")) {
        return (
            <Alert color="warning">
                <p className="mb-0">
                    {t("Please upgrade your plan to access this feature")}.
                </p>
            </Alert>
        );
    }

    return (
        <div className="overview-tab">
            <Nav tabs className="sub-tabs">
                <NavItem>
                    <NavLink
                        className={classnames({ active: activeTab === "screening" })}
                        onClick={() => setActiveTab("screening")}
                    >
                        {t("Compliance Screening")}
                    </NavLink>
                </NavItem>

                <NavItem>
                    <NavLink
                        className={classnames({ active: activeTab === "monitoring" })}
                        onClick={() => setActiveTab("monitoring")}
                    >
                        {t("Compliance Monitoring")}
                    </NavLink>
                </NavItem>

                <NavItem>
                    <NavLink
                        className={classnames({ active: activeTab === "dnb" })}
                        onClick={() => setActiveTab("dnb")}
                    >
                        {t("Dun & Bradstreet")}
                    </NavLink>
                </NavItem>
            </Nav>

            <TabContent color="white" activeTab={activeTab} className="p-3 bg-white">
                <TabPane tabId="screening">
                    <CompliancePoolContent
                        poolKey="screening"
                        showUsageTable
                        fetchQuery={fetchQuery}
                    />
                </TabPane>

                <TabPane tabId="monitoring">
                    <CompliancePoolContent
                        poolKey="monitoring"
                        showUsageTable
                        fetchQuery={fetchQuery}
                    />
                </TabPane>

                <TabPane tabId="dnb">
                    <Dnb />
                </TabPane>
            </TabContent>
        </div>
    );
};

export default withNamespaces()(CompanyInfo);
